import React, { useMemo, useState } from 'react';
import { Form, Select } from 'antd';
import { useGetSelectFieldValues } from '@/shared/api/client/field-configuration-controller';
import type { SelectFieldOption } from '@/shared/api/model';

export interface WardSelectProps {
  disabled?: boolean;
  placeholder?: string;
  form: any;
  districtFieldName?: string;
  value?: string;
  onChange?: (value: string | undefined) => void;
  onBlur?: () => void;
}

/**
 * Ward select component with optional district filtering.
 * Fetches wards from the 'areaCode' provider (wards are stored as area codes).
 * When a district is selected, filters wards by that district.
 *
 * Used for parameter type: WARD_SELECT
 */
const WardSelect: React.FC<WardSelectProps> = ({
  disabled,
  placeholder,
  form,
  districtFieldName = 'district',
  ...rest
}) => {
  const [search, setSearch] = useState<string | undefined>(undefined);

  const districtValue = Form.useWatch(districtFieldName, form);

  const params = useMemo(() => {
    const p: Record<string, unknown> = {
      search,
      fieldName: 'ward',
      entityType: 'REPORT',
    };

    if (districtValue && districtValue !== '-1') {
      p.distCode = districtValue;
    }

    return p;
  }, [search, districtValue]);

  const { data: options, isLoading, error } = useGetSelectFieldValues('areaCode', params as any, {
    query: {
      staleTime: 5 * 60 * 1000,
    },
  });

  const selectOptions = useMemo(() => {
    const list: SelectFieldOption[] = Array.isArray(options) ? options : [];
    return list.map((opt) => ({
      value: opt.value,
      label: opt.label,
      disabled: opt.disabled,
    }));
  }, [options]);

  return (
    <Select
      showSearch
      allowClear
      disabled={disabled}
      loading={isLoading}
      placeholder={placeholder || 'Select ward'}
      options={selectOptions}
      filterOption={false}
      onSearch={(q) => setSearch(q)}
      notFoundContent={error ? 'Failed to load wards' : isLoading ? 'Loading...' : 'No wards'}
      optionFilterProp="label"
      {...rest}
    />
  );
};

export default WardSelect;
